/* eslint-disable @angular-eslint/prefer-inject */
import { Directive, EventEmitter, inject, Input, OnChanges, OnInit, Output } from '@angular/core';
import { debounceTime, firstValueFrom, Subject } from 'rxjs';
import { BaseQuery } from '../../../shared/models/base/base-query';
import { TranslationState } from '../../../shared/enums/translation-state';
import { AccessRightsService } from '../../services/access-rights.service';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes-tracker.service';
import { environment } from '../../../environments/environment';

@Directive()
export abstract class BaseFilter<
  T extends BaseQuery & {
    changesBy?: string;
    translationStates?: TranslationState[];
    translationStatesDocumentation?: TranslationState[];
    translationStatesTerminal?: TranslationState[];
  },
>
  implements OnInit, OnChanges
{
  @Input() public isOpen = false;
  @Output() public filterChanged: Omit<EventEmitter<T>, 'emit'> = new EventEmitter<T>() as EventEmitter<T> as Omit<
    EventEmitter<T>,
    'emit'
  >;
  @Output() public closed = new EventEmitter<void>();

  public searchQuery: T = {} as T;
  public filterHeight = 0;
  public translationState = TranslationState;
  public translationStateDocumentation = TranslationState;
  public translationStateTerminal = TranslationState;
  public translationStates: Map<TranslationState, boolean> = new Map<TranslationState, boolean>();
  public translationStatesDocumentation: Map<TranslationState, boolean> = new Map<TranslationState, boolean>();
  public translationStatesTerminal: Map<TranslationState, boolean> = new Map<TranslationState, boolean>();

  protected readonly accessRightsService = inject(AccessRightsService);
  protected readonly unsavedChangesService = inject(UnsavedChangesTrackerService);

  protected readonly heightOfClosedFilters = 0;
  protected readonly heightOfOpenedFilters = 420 as number;
  protected readonly defaultFilter: T = {} as T;

  private readonly _filterChangedEmitter = this.filterChanged as unknown as EventEmitter<T>;
  private readonly filterChanged$ = new Subject<T>();
  private lastFilterEmitted: T | null = null;

  constructor(heightOfOpenedFilters: number, defaultFilter: T) {
    if (heightOfOpenedFilters !== undefined) {
      this.heightOfOpenedFilters = heightOfOpenedFilters;
    }
    if (defaultFilter !== undefined) {
      this.defaultFilter = defaultFilter;
    }

    this.filterChanged$.pipe(debounceTime(environment.debounceTimespan)).subscribe((filter) => {
      this._filterChangedEmitter.emit(filter);
    });
  }

  public ngOnInit(): void {
    this.initTranslationStates();
  }

  public ngOnChanges(): void {
    this.filterHeight = this.isOpen ? this.heightOfOpenedFilters : this.heightOfClosedFilters;
  }

  public openFilters(): void {
    this.isOpen = true;
    this.filterHeight = this.heightOfOpenedFilters;
  }

  public closeFilters(): void {
    this.isOpen = false;
    this.filterHeight = this.heightOfClosedFilters;
    this.closed.emit();
  }

  public onScopeChanged(): void {
    const blocked = this.unsavedChangesService.areThereAnyUnsavedChanges(
      () => {
        this.emitFilterChanged();
      },
      () => {
        if (this.lastFilterEmitted) {
          this.searchQuery = { ...this.lastFilterEmitted };
        } else {
          this.searchQuery = { ...this.defaultFilter };
        }
      }
    );
    if (!blocked) {
      this.emitFilterChanged();
    }
  }

  public async changesByMe(): Promise<void> {
    const currentUser = await firstValueFrom(this.accessRightsService.getCurrentUser$());
    if (this.searchQuery.changesBy === currentUser) {
      this.searchQuery.changesBy = undefined;
    } else {
      this.searchQuery.changesBy = currentUser;
    }
    this.emitFilterChanged();
  }

  public toggleTranslationState(state: TranslationState): void {
    const current = this.translationStates.get(state) ?? false;
    this.translationStates.set(state, !current);
    this.searchQuery.translationStates = Array.from(this.translationStates.entries())
      .filter((entry) => entry[1])
      .map((entry) => entry[0]);
    this.emitFilterChanged();
  }

  public toggleTranslationStateDocumentation(state: TranslationState): void {
    const current = this.translationStatesDocumentation.get(state) ?? false;
    this.translationStatesDocumentation.set(state, !current);
    this.searchQuery.translationStatesDocumentation = Array.from(this.translationStatesDocumentation.entries())
      .filter((entry) => entry[1])
      .map((entry) => entry[0]);
    this.emitFilterChanged();
  }

  public toggleTranslationStateTerminal(state: TranslationState): void {
    const current = this.translationStatesTerminal.get(state) ?? false;
    this.translationStatesTerminal.set(state, !current);
    this.searchQuery.translationStatesTerminal = Array.from(this.translationStatesTerminal.entries())
      .filter((entry) => entry[1])
      .map((entry) => entry[0]);
    this.emitFilterChanged();
  }

  public resetFilters(): void {
    const blocked = this.unsavedChangesService.areThereAnyUnsavedChanges(() => {
      this.clearAndResetFilters();
    });
    if (!blocked) {
      this.clearAndResetFilters();
    }
  }

  protected getAllTranslationStates(): TranslationState[] {
    return Object.values(TranslationState);
  }

  protected initTranslationStates(): void {
    for (const ts of this.getAllTranslationStates()) {
      if (ts !== TranslationState.MainLanguage) {
        this.translationStates.set(ts, false);
        this.translationStatesDocumentation.set(ts, false);
        this.translationStatesTerminal.set(ts, false);
      }
    }
  }

  protected emitFilterChanged(): void {
    if (this.unsavedChangesService.anyUnsavedChanges() === false) {
      this.lastFilterEmitted = { ...this.searchQuery };
    }
    this.filterChanged$.next({ ...this.searchQuery });
  }

  private clearAndResetFilters(): void {
    this.searchQuery = { ...this.defaultFilter };
    this.initTranslationStates();
    this.emitFilterChanged();
  }
}
